import { useCopyToClipboard } from "react-use";

import { LoaderFunctionArgs, MetaFunction } from "react-router";
import {
  Await,
  ShouldRevalidateFunction,
  useLoaderData,
  useLocation,
  useSearchParams,
} from "react-router";
import React, { Suspense, useEffect } from "react";
import { graphql } from "~/graphql/codegen";
import { getServerClient } from "~/graphql";
import { getAccessToken } from "~/utils/ensure-authenticated.server";
import PageContainer from "~/components/containers/page-container";
import { PageColumn } from "~/components/containers/page-column";
import { Separator } from "@mindstonehq/ui";
import usePageView from "~/hooks/page-view";
import ProfileTop from "~/components/profile/profile-top";
import ProfileLeft from "~/components/profile/profile-left";
import ProfileRight from "~/components/profile/profile-right";
import ProfileOnlyYou from "~/components/profile/profile-only-you";
import ProfileCommunity, {
  ProfileCommunitySkeleton,
} from "~/components/profile/profile-community";
import ProfileVisitorCTA from "~/components/profile/profile-visitor-cta";
import ProfileCoaching from "~/components/profile/profile-coaching";
import ProfileHeroChips from "~/components/profile/profile-hero-chips";
import ProfileImpactStrip, {
  ProfileImpactStripSkeleton,
} from "~/components/profile/profile-impact-strip";
import { fetchCommunitySignals } from "~/lib/community-admin-client.server";
import type { CommunitySignals } from "~/lib/community-signals-types";
import { fetchCoachingSignals } from "~/lib/coaching-admin-client.server";
import type { CoachingSignals } from "~/lib/coaching-signals-types";
import {
  deriveImpactStats,
  groupCertificates,
} from "~/lib/profile-celebration";
import copy from "copy-to-clipboard";
import { toast } from "@mindstonehq/ui";
import { useUser, useIsAuthenticated } from "~/hooks/context-hook";
import { usePlatformAccess } from "~/hooks/platform-access";
import {
  UserBillingStatus,
  type ProfileDetailedInfoFragment,
  type StatsProfileFragment,
} from "~/graphql/codegen/graphql";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { i18n } from "@lingui/core";
import { displayNameOf } from "~/utils/display-name";

// Fragments
const LearningObjectiveFragment = graphql(`
  fragment LearningObjective on LearningObjective {
    id
    name
    icon
    objective
    explanation
  }
`);

const BasicProfileFragment = graphql(`
  fragment ProfileDetailedInfo on Profile {
    id
    username
    profilePictureURL
    displayName
    firstName
    mine
    tagLine
    profileUrl
    bio
    following
    followers(size: 1) {
      total
    }
    followings(size: 1) {
      total
    }
    level {
      label
      level
    }
    programs {
      id
      metadata {
        title
        description
      }
      badgeEarned
    }
  }
`);

const AchievementFragment = graphql(`
  fragment AchievementDetailed on Achievement {
    id
    achievementID
    iconURL
    awardedOn
    progress
    target
    shareURL
    objective {
      ...LearningObjective
    }
  }
`);

const StatsProfileFragment = graphql(`
  fragment statsProfile on Profile {
    achievements {
      achievements {
        ...AchievementDetailed
      }
    }
  }
`);

// Queries
const ProfileDocument = graphql(`
  query Profile($username: String!) {
    profile(username: $username) {
      ...ProfileDetailedInfo
    }
  }
`);

const StatsProfileDocument = graphql(`
  query StatsProfile($username: String!) {
    profile(username: $username) {
      ...statsProfile
    }
  }
`);

export const shouldRevalidate: ShouldRevalidateFunction = ({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}) => {
  if (currentUrl.pathname === nextUrl.pathname) {
    // If it's exactly the same path name, it means we are changing the search params. No need to revlidate, right?
    return false;
  }
  return defaultShouldRevalidate;
};

export const meta: MetaFunction<typeof loader> = (loaderData) => {
  const profile = loaderData?.data?.profile;
  if (!profile) {
    // eslint-disable-next-line lingui/no-unlocalized-strings -- brand SEO title
    return [{ title: " Mindstone" }];
  }
  const ogImage = loaderData?.data?.ogImageUrl || profile.profilePictureURL;
  const displayName = profile.displayName || profile.username;
  const profileTitle = i18n._(msg`${displayName}'s profile`);
  const defaultDescription = i18n._(
    msg`${displayName}'s profile on Mindstone. Discover their top skills and what they're currently learning.`,
  );
  return [
    {
      title: profileTitle,
    },
    {
      name: "description",
      content: profile.bio || defaultDescription,
    },
    {
      property: "og:site_name",
      // eslint-disable-next-line lingui/no-unlocalized-strings -- brand name
      content: "Mindstone",
    },
    {
      property: "og:title",
      content: profileTitle,
    },
    {
      property: "og:description",
      content: profile.bio || defaultDescription,
    },
    {
      property: "og:image",
      content: ogImage,
    },
    {
      property: "og:image:url",
      content: ogImage,
    },
    { property: "og:image:type", content: "image/png" },
    ...(loaderData?.data?.ogImageUrl
      ? [
          { property: "og:image:width", content: "1200" },
          { property: "og:image:height", content: "630" },
        ]
      : []),
    {
      property: "og:url",
      content: `https://app.mindstone.com/profile/${profile.username}`,
    },
    {
      property: "twitter:card",
      content: "summary_large_image",
    },
    {
      property: "twitter:site",
      // eslint-disable-next-line lingui/no-unlocalized-strings -- twitter handle
      content: "@MindstoneHQ",
    },
    {
      property: "twitter:creator",
      // eslint-disable-next-line lingui/no-unlocalized-strings -- twitter handle
      content: "@MindstoneHQ",
    },
    {
      property: "twitter:title",
      content: profileTitle,
    },
    {
      property: "twitter:description",
      content: profile.bio || defaultDescription,
    },
    {
      property: "twitter:image",
      content: ogImage,
    },
    {
      property: "twitter:image:src",
      content: ogImage,
    },
    {
      property: "twitter:cta",
      content: i18n._(msg`View in Mindstone`),
    },
    { property: "twitter:widgets:new-embed-design", content: "on" },
    { property: "twitter:widgets:csp", content: "on" },
    { property: "og:type", content: "profile" },
    {
      property: "og:profile:username",
      content: profile.username,
    },
  ];
};

export async function loader({ request, params }: LoaderFunctionArgs) {
  const accessToken = await getAccessToken(request);
  const username = params["username"];

  if (!username) {
    throw new Response(null, {
      status: 404,
      statusText: "Not Found",
    });
  }
  const url = new URL(request.url);

  const { data: profileData, error } = await getServerClient()
    .query({
      query: ProfileDocument,
      variables: {
        username,
      },
      fetchPolicy: "no-cache",
      context: {
        headers: {
          Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        },
      },
    })
    .catch((error) => {
      throw error;
    });
  if (error) {
    throw error;
  }
  if (!profileData?.profile) {
    throw new Response(null, {
      status: 404,
      statusText: "Not Found",
    });
  }
  const profile = profileData.profile;
  const statsPromise = getServerClient()
    .query({
      query: StatsProfileDocument,
      variables: {
        username,
      },
      fetchPolicy: "no-cache",
      context: {
        headers: {
          Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        },
      },
    })
    .catch((error) => {
      throw error;
    });
  // Deferred, best-effort: fetchCommunitySignals never throws (it resolves to
  // null on any failure), so the profile page always renders even if
  // community-admin is unreachable.
  const communitySignalsPromise: Promise<CommunitySignals | null> =
    fetchCommunitySignals({
      username: profile.username,
      mindstoneUserId: profile.id,
      request,
    });

  // Independent, best-effort coaching signals (coach badge + sessions
  // delivered). Same contract as community: resolves to null on any failure so
  // the section is simply hidden and the page always renders.
  const coachingSignalsPromise: Promise<CoachingSignals | null> =
    fetchCoachingSignals({
      username: profile.username,
      mindstoneUserId: profile.id,
      request,
    });

  // Dynamic OG card URL (community-admin renders a branded PNG of the verified
  // community signals). Built server-side so the public community-admin host is
  // resolved from env here, not in `meta` (which also runs on the client).
  const communityAdminPublicUrl = process.env.COMMUNITY_ADMIN_URL?.replace(
    /\/$/,
    "",
  );
  // Pass the existing profile picture as a fallback so the OG endpoint can
  // redirect to it if its render fails or community-admin is unreachable —
  // otherwise a broken share image would replace the pre-feature behaviour
  // (og:image used to be the profile picture).
  const ogImageUrl = communityAdminPublicUrl
    ? `${communityAdminPublicUrl}/api/public/profile-og/${encodeURIComponent(
        profile.username,
      )}?id=${encodeURIComponent(profile.id)}${
        profile.profilePictureURL
          ? // eslint-disable-next-line lingui/no-unlocalized-strings -- URL query fragment
            `&fallback=${encodeURIComponent(profile.profilePictureURL)}`
          : ""
      }`
    : null;

  return {
    profile,
    statsPromise,
    communitySignalsPromise,
    coachingSignalsPromise,
    ogImageUrl,
  };
}

export default function route() {
  const { t } = useLingui();
  usePageView("Profile");
  const { profile, statsPromise, communitySignalsPromise, coachingSignalsPromise } =
    useLoaderData<typeof loader>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const user = useUser();
  const copyReferralCode = searchParams.get("copyReferralCode") === "true";
  const referralCode = user?.referrals?.[0]?.referralCode;

  useEffect(() => {
    if (copyReferralCode && referralCode && user?.id === profile.id) {
      copy(referralCode);
      toast.success(t(msg`Discount code copied to clipboard`));
    }
  }, [copyReferralCode, referralCode, user?.id, profile.id, t]);

  const title = React.useMemo(() => {
    if (
      location.pathname.includes("following") ||
      location.pathname.includes("followers")
    ) {
      return undefined;
    }
    return t(msg`${displayNameOf(profile)}'s profile`);
  }, [location.pathname, profile.displayName, profile.username, t]);

  return (
    <PageContainer title={title} className="content-start">
      <Suspense
        fallback={
          <Profile
            profile={profile}
            communitySignalsPromise={communitySignalsPromise}
            coachingSignalsPromise={coachingSignalsPromise}
          />
        }
        key={profile.username}
      >
        <Await resolve={statsPromise}>
          {(result) => (
            <Profile
              profile={profile}
              stats={result.data?.profile}
              communitySignalsPromise={communitySignalsPromise}
              coachingSignalsPromise={coachingSignalsPromise}
            />
          )}
        </Await>
      </Suspense>
    </PageContainer>
  );
}

function Profile({
  profile,
  stats,
  communitySignalsPromise,
  coachingSignalsPromise,
}: {
  profile: ProfileDetailedInfoFragment;
  stats?: StatsProfileFragment | null;
  communitySignalsPromise: Promise<CommunitySignals | null>;
  coachingSignalsPromise: Promise<CoachingSignals | null>;
}) {
  const user = useUser();
  const isAuthenticated = useIsAuthenticated();
  const isCurrentUser = profile.id === user?.id;
  // The existing condition is preserved LITERALLY and the self-serve
  // entitlement is APPENDED as a third alternative — replacing it with
  // usePlatformAccess() alone would have deleted referral-only access for
  // learners who have a referral code but no active subscription. The ACTIVE
  // term is kept even though usePlatformAccess() also covers it, so the
  // before/after formulas stay mechanically comparable. Strictly ADDITIVE:
  // a lapsed-Plus Live learner with no referral code still sees nothing.
  const hasPlatformAccess = usePlatformAccess();
  const hasOnlyYouWidgets =
    isCurrentUser &&
    (user?.billing?.status === UserBillingStatus.Active ||
      Boolean(user?.referrals?.[0]?.referralCode) ||
      hasPlatformAccess);
  const certificateGroups = React.useMemo(
    () => groupCertificates(stats?.achievements?.achievements),
    [stats?.achievements?.achievements],
  );
  const heroSignalsPromise = React.useMemo(
    () =>
      Promise.all([communitySignalsPromise, coachingSignalsPromise]).then(
        ([signals, coaching]) => ({ signals, coaching }),
      ),
    [communitySignalsPromise, coachingSignalsPromise],
  );

  return (
    <>
      <PageColumn className="col-span-12 flex flex-col overflow-y-visible">
        <ProfileTop
          profile={profile}
          chipsSlot={
            <Suspense fallback={null}>
              <Await resolve={heroSignalsPromise} errorElement={null}>
                {({ signals, coaching }) => (
                  <ProfileHeroChips
                    signals={signals}
                    coaching={coaching}
                    certificateGroups={certificateGroups}
                  />
                )}
              </Await>
            </Suspense>
          }
        />
        <Separator />
      </PageColumn>
      <PageColumn className="col-span-12 overflow-y-visible">
        <Suspense fallback={<ProfileImpactStripSkeleton />}>
          <Await resolve={communitySignalsPromise} errorElement={null}>
            {(signals) => (
              <ProfileImpactStrip
                stats={deriveImpactStats(signals, certificateGroups, {
                  isCurrentUser,
                })}
              />
            )}
          </Await>
        </Suspense>
      </PageColumn>
      <PageColumn className="col-span-12 sm:col-span-7 flex flex-col gap-8 justify-start overflow-y-visible">
        {!isAuthenticated && (
          <ProfileVisitorCTA
            displayName={profile.displayName || profile.username}
            username={profile.username}
          />
        )}
        <Suspense fallback={<ProfileCommunitySkeleton />}>
          <Await resolve={communitySignalsPromise} errorElement={null}>
            {(signals) => (
              <ProfileCommunity
                signals={signals}
                isCurrentUser={isCurrentUser}
                firstName={profile.firstName}
              />
            )}
          </Await>
        </Suspense>
        <ProfileLeft profile={profile} />
      </PageColumn>
      <PageColumn className="col-span-12 sm:col-span-5 overflow-y-visible">
        <div id="profile-credentials">
          <ProfileRight profile={profile} stats={stats} />
        </div>
        <Suspense fallback={null}>
          <Await resolve={coachingSignalsPromise} errorElement={null}>
            {(signals) => (
              <ProfileCoaching
                signals={signals}
                isCurrentUser={isCurrentUser}
              />
            )}
          </Await>
        </Suspense>
      </PageColumn>
      {hasOnlyYouWidgets && (
        <PageColumn className="col-span-12 overflow-y-visible">
          <ProfileOnlyYou profile={profile} />
        </PageColumn>
      )}
    </>
  );
}
